// The automatic part of a pit stop (DESIGN.md §5 v2): once the player has
// committed to the pit entry road, the car brakes to the 80 km/h limit
// (B1.6.3), drives down the lane, swings into its box, stops for service and
// drives out, handing control back where the exit road meets the track.

import type { PitLane } from '../track/pitLane';

export type PitStage = 'entry' | 'limiter' | 'stopping' | 'stationary' | 'leaving' | 'exit' | 'done';

export interface PitFrame {
  x: number;
  z: number;
  y: number;
  heading: number;
  speed: number;
}

const ENTRY_DECEL = 22; // m/s², hard braking on the entry road
const BOX_DECEL = 8; // m/s², rolling into the box
const LEAVE_ACCEL = 7; // m/s², out of the box
const EXIT_ACCEL = 11; // m/s², down the exit road
const EXIT_SPEED = 45; // m/s cap on the exit road (~160 km/h), so control comes back calmly

export class PitStop {
  stage: PitStage = 'entry';
  d: number;
  speed: number;
  stationaryLeft = 0;
  private readonly limit: number;

  constructor(
    readonly lane: PitLane,
    startD: number,
    startSpeed: number,
    /** Called once as the car stops in the box; returns the stationary time. */
    private readonly onStop: () => number,
  ) {
    this.d = startD;
    this.speed = startSpeed;
    this.limit = lane.speedLimitKmh / 3.6;
  }

  get done(): boolean {
    return this.stage === 'done';
  }

  /** In the speed-limited part of the lane (limiter on). */
  get limiterOn(): boolean {
    return this.d >= this.lane.speedLimitStart && this.d < this.lane.speedLimitEnd;
  }

  step(dt: number): PitFrame {
    const L = this.lane;
    const v80 = this.limit;
    switch (this.stage) {
      case 'entry': {
        // Brake so we cross the pit entry line at the limit.
        // Keep racing down the entry road, braking just in time for the line.
        const room = Math.max(0, L.speedLimitStart - this.d);
        const brakeCap = Math.sqrt(v80 * v80 + 2 * ENTRY_DECEL * room);
        this.speed = Math.min(this.speed + EXIT_ACCEL * dt, EXIT_SPEED, brakeCap);
        // Never cross the line above the limit, even within one step.
        if (this.d + this.speed * dt >= L.speedLimitStart) this.speed = Math.min(this.speed, v80);
        if (this.d >= L.speedLimitStart) this.stage = 'limiter';
        break;
      }
      case 'limiter': {
        const toBox = L.boxAt - this.d;
        const stopping = Math.sqrt(2 * BOX_DECEL * Math.max(0, toBox));
        // Hold the limit, then roll to a stop on the box marks (creeping the last few cm).
        const accelerating = Math.min(v80, this.speed + LEAVE_ACCEL * dt);
        this.speed = Math.max(Math.min(accelerating, stopping), Math.min(0.6, toBox * 4));
        if (toBox <= 0.05) {
          this.d = L.boxAt;
          this.speed = 0;
          this.stage = 'stationary';
          this.stationaryLeft = this.onStop();
        }
        break;
      }
      case 'stationary':
        this.speed = 0;
        this.stationaryLeft -= dt;
        if (this.stationaryLeft <= 0) this.stage = 'leaving';
        break;
      case 'leaving':
        this.speed = Math.min(v80, this.speed + LEAVE_ACCEL * dt);
        if (this.d >= L.speedLimitEnd) this.stage = 'exit';
        break;
      case 'exit':
        this.speed = Math.min(EXIT_SPEED, this.speed + EXIT_ACCEL * dt);
        if (this.d >= L.merge.handbackD) this.stage = 'done';
        break;
      case 'done':
        break;
    }
    if (this.stage !== 'stationary') this.d = Math.min(L.length, this.d + this.speed * dt);

    // Pose on the path, swung sideways into the box; heading follows the swing.
    const lat = L.boxLateralAt(this.d);
    const pose = L.poseAt(this.d, lat);
    const slope = (L.boxLateralAt(this.d + 0.5) - L.boxLateralAt(this.d - 0.5)) / 1;
    return { x: pose.x, z: pose.z, y: pose.y, heading: pose.heading + Math.atan(slope), speed: this.speed };
  }
}
